import React, { useState } from 'react';
import { Heart, Sparkles, Crown, Star, CheckCircle } from 'lucide-react';
import PageLayout, { PageHero, PageSection, PageCTA } from '../components/common/PageLayout';
import SEOHead from '../components/common/SEOHead';
import { useLanguage } from '../i18n/LanguageContext';

const GALLERY = [
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/wjkmdoh0_394EB39E-BAE8-427B-8173-080A499032F7.png', title: 'Boda Premium' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/1t6l7xrw_784188E3-94EC-4024-AA54-F027CA0D9874.png', title: 'Celebración Romántica' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/d5e1gvds_IMG_0683.jpeg', title: 'Arte en el Cielo' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/5q8imokm_IMG_0671.webp', title: 'Espiral de Luz' },
];

const benefitIcons = [Heart, Crown, Sparkles, Star];

const EventosPrivadosPage = () => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const { language } = useLanguage();

  const content = {
    es: {
      title: "Eventos Privados",
      titleHighlight: "Premium",
      subtitle: "Transforma tus celebraciones más especiales en momentos mágicos e inolvidables.",
      description: "Bodas, aniversarios, cumpleaños VIP y celebraciones exclusivas con experiencias de clase mundial.",
      whyChoose: "Experiencias",
      whyChooseHighlight: "de Lujo",
      benefits: [
        { title: "Momento Único", desc: "Crea un recuerdo que tú y tus invitados guardarán para siempre." },
        { title: "Exclusividad Total", desc: "Shows personalizados con mensajes, fechas y figuras únicas." },
        { title: "Producción Premium", desc: "Equipo profesional que cuida cada detalle de tu evento." },
        { title: "Contenido Memorable", desc: "Fotos y videos espectaculares para compartir." },
      ],
      occasions: "Ocasiones",
      occasionsHighlight: "Perfectas",
      occasionsList: [
        "Bodas y compromisos de ensueño",
        "Propuestas de matrimonio espectaculares",
        "Aniversarios inolvidables",
        "Cumpleaños VIP y XV años",
        "Fiestas privadas exclusivas",
        "Celebraciones familiares especiales",
      ],
      gallery: "Momentos",
      galleryHighlight: "Mágicos",
      ctaTitle: "¿Planeas una celebración",
      ctaTitleHighlight: "especial",
      ctaDescription: "Hagamos de tu evento un momento mágico que jamás olvidarás.",
    },
    en: {
      title: "Premium",
      titleHighlight: "Private Events",
      subtitle: "Transform your most special celebrations into magical and unforgettable moments.",
      description: "Weddings, anniversaries, VIP birthdays and exclusive celebrations with world-class experiences.",
      whyChoose: "Luxury",
      whyChooseHighlight: "Experiences",
      benefits: [
        { title: "Unique Moment", desc: "Create a memory that you and your guests will treasure forever." },
        { title: "Total Exclusivity", desc: "Personalized shows with unique messages, dates and figures." },
        { title: "Premium Production", desc: "Professional team that takes care of every detail of your event." },
        { title: "Memorable Content", desc: "Spectacular photos and videos to share." },
      ],
      occasions: "Perfect",
      occasionsHighlight: "Occasions",
      occasionsList: [
        "Dream weddings and engagements",
        "Spectacular marriage proposals",
        "Unforgettable anniversaries",
        "VIP birthdays and quinceañeras",
        "Exclusive private parties",
        "Special family celebrations",
      ],
      gallery: "Magical",
      galleryHighlight: "Moments",
      ctaTitle: "Planning a special",
      ctaTitleHighlight: "celebration",
      ctaDescription: "Let's make your event a magical moment you'll never forget.",
    }
  };

  const c = content[language] || content.es;

  return (
    <PageLayout>
      <SEOHead 
        title={language === 'es' ? "Eventos Privados Premium | Bodas con Drones | NexAir" : "Premium Private Events | Weddings with Drones | NexAir"}
        description={language === 'es' ? "Shows de drones para bodas, aniversarios y eventos privados exclusivos. Experiencias de lujo que transforman celebraciones en momentos mágicos." : "Drone shows for weddings, anniversaries and exclusive private events. Luxury experiences that transform celebrations into magical moments."}
        keywords={language === 'es' ? "boda con drones, evento privado drones, propuesta matrimonio drones, fiesta vip drones" : "wedding with drones, private event drones, marriage proposal drones, vip party drones"}
        canonicalUrl="https://nexair.com.mx/eventos-privados"
      />
      
      <PageHero 
        image="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/wjkmdoh0_394EB39E-BAE8-427B-8173-080A499032F7.png"
        title={c.title}
        titleHighlight={c.titleHighlight}
        subtitle={c.subtitle}
        description={c.description}
        gradient="from-pink-600/30 to-purple-600/30"
      />
      
      {/* Benefits */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center font-heading">
          {c.whyChoose} <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-500">{c.whyChooseHighlight}</span>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {c.benefits.map((benefit, index) => {
            const Icon = benefitIcons[index];
            return (
              <div key={index} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-pink-500/50 transition-all">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-pink-500/20 to-purple-500/20 flex items-center justify-center mb-4">
                  <Icon className="w-7 h-7 text-pink-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 font-heading">{benefit.title}</h3>
                <p className="text-gray-400">{benefit.desc}</p>
              </div>
            );
          })}
        </div>
      </PageSection>

      {/* Occasions */}
      <PageSection className="bg-[#050a15]">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 font-heading">
              {c.occasions} <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-500">{c.occasionsHighlight}</span>
            </h2>
            <ul className="space-y-4">
              {c.occasionsList.map((item, index) => (
                <li key={index} className="flex items-center gap-4 text-lg text-gray-300">
                  <CheckCircle className="w-6 h-6 text-pink-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img 
              src="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/1t6l7xrw_784188E3-94EC-4024-AA54-F027CA0D9874.png"
              alt="Private Event"
              className="rounded-2xl shadow-2xl"
            />
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-pink-600/20 to-transparent" />
          </div>
        </div>
      </PageSection>
      
      {/* Gallery */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center font-heading">
          {c.gallery} <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-500">{c.galleryHighlight}</span>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {GALLERY.map((img, index) => (
            <div 
              key={index}
              className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group"
              onClick={() => setLightboxImage(img.url)}
            >
              <img 
                src={img.url}
                alt={img.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <span className="text-white font-medium">{img.title}</span>
              </div>
            </div>
          ))}
        </div>
      </PageSection>
      
      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <img src={lightboxImage} alt="Gallery" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
          <button className="absolute top-6 right-6 text-white/70 hover:text-white text-4xl" onClick={() => setLightboxImage(null)}>×</button>
        </div>
      )}
      
      <PageCTA 
        title={c.ctaTitle}
        titleHighlight={c.ctaTitleHighlight}
        description={c.ctaDescription}
        gradient="from-pink-600 to-purple-600"
      />
    </PageLayout>
  );
};

export default EventosPrivadosPage;
